import animejs from 'animejs/lib/anime.min.js';
import FormController from "./form-controller";
import FormFieldController from "./form-field-controller";
import {emailValidator, trueValidator} from "./validators";
import TextField from "./fields/text-field";
import FormEventSubscription from "./form-event-subscription";
import Api from "../../../lib/api";
import {FormCacheCookieStorage} from "./form-cache-storage";
import AgreementField from "./fields/agreement-field";
import SubmitButton from "./components/submit-button";
import FormModule from "./form-module";
import RadioField from "./fields/radio-field";
import CheckboxField from "./fields/checkbox-field";
import TelField from "./fields/tel-field";
import SelectField from "./fields/select-field";
import {PaymentFormatField} from "./fields/payment-format-field";
import PromocodeField from "./fields/promocode-field";
import ConsultationField from "./fields/consultation-field";
import SocialsField from "./fields/socials-field";
import CourseField from "./fields/course-field";

export default class Form {
    constructor(props = {}) {
        this.props = props;

        this.id = this.props.id;
        this.controller = this.props.controller;
        this.fieldControllers = this.props.fieldControllers || [];
        this.subscriptions = this.props.subscriptions || [];
        this.fieldOptions = this.props.fieldOptions || {};
        this.moduleControllerClasses = this.props.moduleControllerClasses || {};
        this.showResultScreen = this.props.showResultScreen || true;

        this.$root = document.getElementById(`form-${this.id}`);
        this.$body = null;
        this.$modules = null;
        this.$fields = null;
        this.$submitButtons = null;
        this.$formButtons = null;

        this._fields = new Map();
        this._modules = new Map();
        this._submitButtons = [];

        this._activeModuleName = null;

        this.SUBMIT_ANIMATION_COMPLETE_EVENT = 'submitAnimationComplete';
        this.ACTIVE_MODULE_CHANGE_EVENT = 'activeModuleChange';

        this.init();
    }

    get eventEmitter() {
        if (!this.controller) {
            throw new Error('Form: Controller is not defined!');
        }

        return this.controller.eventEmitter;
    }

    get activeModuleName() {
        return this._activeModuleName;
    }

    init() {
        this._checkProps();
        this._beforeInit();
        this._initElements();
        this._initFields();
        this._initController();
        this._initComponents();
        this._addSubscriptions();
        this._validate();
        this._afterInit();
    }

    getField(name) {
        return this._fields.get(name);
    }

    syncField(name) {
        const field = this._fields.get(name);

        if (!field) {
            return;
        }

        field.sync();
    }

    reinitField(name, $root) {
        const field = this._fields.get(name);

        if (!field) {
            return;
        }

        field.reinit($root);
    }

    getFieldValue(name) {
        return this.controller.getFieldValue(name);
    }

    getSubmitData() {
        return this.controller.getSubmitData();
    }

    disableSubmitButton() {
        this._submitButtons.forEach((submitButton) => submitButton.setDisabled());
    }

    enableSubmitButton() {
        this._submitButtons.forEach((submitButton) => submitButton.setDisabled(false));
    }

    changeModule(name) {
        this._handleModuleChange({ name });
    }

    _beforeInit() {}

    _afterInit() {}

    _checkProps() {
        if (!this.$root) {
            throw new Error('Form: Root element not found');
        }

        if (this.fieldControllers.some((field) => !(field instanceof FormFieldController))) {
            throw new Error('FormController: Fields must be instances of FormFieldController!');
        }

        if (this.subscriptions.some((subscription) => !(subscription instanceof FormEventSubscription))) {
            throw new Error('FormController: Subscriptions must be instances of FormEventSubscription!');
        }
    }

    _initElements() {
        this.$body = this.$root.querySelector('.f-form-body');
        this.$modules = this.$root.querySelectorAll('[data-form="module"]');
        this.$fields = this.$root.querySelectorAll('[data-form="field"]');
        this.$submitButtons = this.$root.querySelectorAll('[data-form="submit"]');
        this.$formButtons = this.$root.querySelectorAll('[data-form="button"]');

        if (!this.$body) {
            throw new Error('Form: Body element not found!');
        }

        if (!this.$modules.length) {
            throw new Error('Form: Main module not found!');
        }

        if (!Array.from(this.$modules).some(($module) => $module.dataset.name === 'main')) {
            throw new Error('Form: Main module not found!');
        }

        if (!this.$fields.length) {
            throw new Error('Form: Field elements not found!');
        }

        if (!this.$submitButtons.length) {
            throw new Error('Form: Submit button not found!');
        }
    }

    _initFields() {
        if (this.controller && this.controller instanceof FormController) {
            return;
        }

        this.$fields.forEach(($field) => {
            const field = this._initField($field);
            this._fields.set(field.name, field);
        });
    }

    _initField($field) {
        const name = $field.dataset.name;
        const type = $field.dataset.type;
        const required = $field.dataset.required === 'true';
        const disabled = $field.dataset.disabled === 'true';
        const cacheable = $field.dataset.cacheable === 'true';
        const unsendable = $field.dataset.unsendable === 'true';
        const pattern = $field.dataset.pattern;

        const fieldOptions = this.fieldOptions[name] || {};
        let controller = this.fieldControllers.find((field) => field.name === name);

        if (!controller) {
            const validators = [];

            switch (type) {
                case 'tel':
                    break;
                case 'email':
                    if (pattern) {
                        validators.push(new RegExp(pattern.trim()));
                        break;
                    }

                    validators.push(emailValidator);
                    break;
                case 'agreement':
                    validators.push(trueValidator);
                    break;
                default:
                    if (pattern) {
                        validators.push(new RegExp(pattern.trim()));
                        break;
                    }

                    break;
            }

            controller = new FormFieldController({
                name,
                required,
                disabled,
                cacheable,
                unsendable,
                validators
            });
        }

        const config = {
            ...fieldOptions,
            $root: $field,
            controller
        };

        switch (type) {
            case 'tel':
                return new TelField(config);
            case 'checkbox':
                return new CheckboxField(config);
            case 'radio':
                return new RadioField(config);
            case 'select':
                return new SelectField(config);
            case 'agreement':
                return new AgreementField(config);
            case 'payment-format':
                return new PaymentFormatField(config);
            case 'promocode':
                return new PromocodeField(config);
            case 'consultation':
                return new ConsultationField(config);
            case 'socials':
                return new SocialsField(config);
            case 'course':
                return new CourseField(config);
            default:
                return new TextField(config);
        }
    }

    _initController() {
        if (this.controller && this.controller instanceof FormController) {
            return;
        }

        this.controller = new FormController(Object.assign({}, this.props, {
            baseUrl: this.props.baseUrl || Api.restSiteUrl,
            cacheStorage: this.props.cacheStorage || new FormCacheCookieStorage({ name: 'h_formValues'}),
            cacheEnabled: this.props.cacheEnabled || true,
            fieldControllers: [
                ...this.fieldControllers.filter((field) => !this._fields.has(field.name)),
                ...Array.from(this._fields.values()).map((field) => field.controller)
            ],
            subscriptions: [
                ...this.subscriptions.filter((subscription) => !subscription.module),
                new FormEventSubscription({
                    event: 'validationStatusChange',
                    callback: this._handleValidationStatusChange.bind(this)
                }),
                new FormEventSubscription({
                    event: 'submitComplete',
                    callback: this._handleSubmitCompleted.bind(this)
                })
            ]
        }));
    }

    _initComponents() {
        this._submitButtons = Array.from(this.$submitButtons).map(($submitButton) => new SubmitButton({
            $root: $submitButton,
            subscriptions: [
                new FormEventSubscription({
                    event: 'submitStart',
                    callback: this._handleSubmit.bind(this)
                })
            ]
        }));

        this.$modules.forEach(($module) => {
            const name = $module.dataset.name;
            const active = $module.dataset.active === 'true';

            if (!name) {
                throw new Error('Form: Module name is not defined!');
            }

            const $moduleFields = Array.from($module.querySelectorAll('[data-form="field"]'));
            const fieldControllers = $moduleFields.map(($field) => {
                const fieldName = $field.dataset.name;
                const field = this._fields.get(fieldName);

                if (!field) {
                    throw new Error(`Form: Field ${fieldName} not found!`);
                }

                return field.controller;
            });

            let ModuleController = FormModule;

            if (this.moduleControllerClasses[name] && this.moduleControllerClasses[name] instanceof FormModule) {
                ModuleController = this.moduleControllerClasses[name];
            }

            this._modules.set(name, new ModuleController({
                $root: $module,
                name: name,
                fieldControllers,
                subscriptions: [
                    ...this.subscriptions.filter((subscription) => subscription.module === name),
                    new FormEventSubscription({
                        event: 'activeModuleChange',
                        callback: this._handleModuleChange.bind(this)
                    })
                ]
            }));

            if (active) {
                this._activeModuleName = name;
                this._setActiveModuleClass(name);
                this.eventEmitter.dispatch(this.ACTIVE_MODULE_CHANGE_EVENT, { name });
            }
        });
    }

    _addSubscriptions() {}

    _validate() {
        this.controller.validate();
    }

    _handleValidationStatusChange(valid) {
        this.$submitButtons.forEach(($submitButton) => {
            $submitButton.disabled = !valid;
        });

        if (this.$formButtons && this.$formButtons.length) {
            this.$formButtons.forEach(($formButton) => {
                if ($formButton.dataset.validate === 'true') {
                    $formButton.disabled = !valid;
                }
            });
        }
    }

    _handleSubmit() {
        this._submitButtons.forEach((submitButton) => {
            submitButton.setDisabled();
        });
        this.controller.submit(
            Promise.all(this._submitButtons.map((submitButton) => submitButton.animate())));
    }

    _handleSubmitCompleted(response) {
        if (response.status === 'fulfilled') {
            Promise.all(this._submitButtons.map((submitButton) => submitButton.endAnimation()))
                .then(() => {
                    if (this.showResultScreen && this._modules.has('result-success')) {
                        this._handleModuleChange({ name: 'result-success' });
                    }

                    this.eventEmitter.dispatch(this.SUBMIT_ANIMATION_COMPLETE_EVENT);
                    this.controller.setInitialValue();
                    this._validate();
                });
        } else {
            Promise.all(this._submitButtons.map((submitButton) => submitButton.interruptAnimation()))
                .then(() => {
                    if (this.showResultScreen && this._modules.has('result-failure')) {
                        this._handleModuleChange({ name: 'result-failure' });
                    }

                    this._validate();
                });
        }
    }

    _handleModuleChange({ name }) {
        const module = this._modules.get(name);
        const activeModule = this._modules.get(this._activeModuleName);

        if (!module) {
            throw new Error(`Form: Module ${name} not found!`);
        }

        if (!activeModule) {
            throw new Error(`Form: Module ${this._activeModuleName} not found!`);
        }

        if (name === this._activeModuleName) {
            return;
        }

        this._setActiveModuleClass(name);
        this.eventEmitter.dispatch(this.ACTIVE_MODULE_CHANGE_EVENT, { name, prevName: this._activeModuleName });

        this._activeModuleName = name;
        this.$body.style.height = `${activeModule.$root.offsetHeight}px`;
        activeModule.$root.removeAttribute('data-active');
        module.$root.style.opacity = 0;
        module.$root.setAttribute('data-active', true);

        animejs({
            targets: this.$body,
            height: `${module.$root.offsetHeight}px`,
            duration: 250,
            easing: 'cubicBezier(0, 0.55, 0.45, 1)',
            complete: (anim) => {
                if (anim.completed) {
                    this.$body.removeAttribute('style');
                }
            }
        });
        animejs({
            targets: module.$root,
            opacity: 1,
            scale: [0.97, 1],
            duration: 250,
            delay: 250,
            easing: 'cubicBezier(0, 0.55, 0.45, 1)',
            complete: (anim) => {
                if (anim.completed) {
                    module.$root.removeAttribute('style');
                }
            }
        });
    }

    _setActiveModuleClass(name) {
        this.$modules.forEach(($module) => {
            if ($module.dataset.name) {
                $module.classList.remove(`-${$module.dataset.name}`);
            }
        });
        this.$root.classList.add(`-${name}`);
    }
}